/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Set guardrails", description = "Control what agents can change in ad and email images, set AI disclosure and ad size defaults, define email rendering rules, and create agents that review ad creative.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Set guardrails",
        description: "Control what agents can change in ad and email images, set AI disclosure and ad size defaults, define email rendering rules, and create agents that review ad creative.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Set ad guardrails",
          "depth": 0,
          "id": "set-ad-guardrails"
        }, {
          "value": "Control image modifications",
          "depth": 1,
          "id": "control-image-modifications"
        }, {
          "value": "Limit changes for specific asset collections",
          "depth": 1,
          "id": "limit-changes-for-specific-asset-collections"
        }, {
          "value": "Set AI disclosure defaults",
          "depth": 1,
          "id": "set-ai-disclosure-defaults"
        }, {
          "value": "Set ad sizes and channels",
          "depth": 1,
          "id": "set-ad-sizes-and-channels"
        }, {
          "value": "Set email guardrails",
          "depth": 0,
          "id": "set-email-guardrails"
        }, {
          "value": "Set the email layout",
          "depth": 1,
          "id": "set-the-email-layout"
        }, {
          "value": "Control email image modifications",
          "depth": 1,
          "id": "control-email-image-modifications"
        }, {
          "value": "Set email HTML rules",
          "depth": 1,
          "id": "set-email-html-rules"
        }, {
          "value": "Create agent reviewers",
          "depth": 0,
          "id": "create-agent-reviewers"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      ol: "ol"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketing teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["The ", _jsxs(_components.a, {
                    href: "/workspace-management/roles#agents",
                    children: [_jsx(_components.strong, {
                      children: "Configure Agents"
                    }), " grant"]
                  }), "."]
                }), _jsx("li", {
                  children: "Ad Studio or Lifecycle Studio."
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Control how agents can change images in ads and emails, set ad and email defaults, and create agent reviewers for ad creative."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["You manage guardrails in ", _jsx(_components.strong, {
          children: "Context Hub > Guardrails"
        }), ". Which tabs you see depends on the products in your workspace:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ads"
          }), " — Image rules, AI disclosure defaults, and ad sizes for Ad Studio. Requires Ad Studio."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Emails"
          }), " — Layout, image rules, and HTML best practices for Lifecycle Studio. Requires Lifecycle Studio."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Agent reviewers"
          }), " — Agents your team can ask to review ad creative in Ad Studio comments. Requires Ad Studio."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each guardrail applies at one of these levels:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "One brand"
          }), " — The brand's image generation setting, AI disclosure defaults, and agent reviewers. If your workspace has more than one brand profile, select the brand in the brand menu on the tab before you change them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "One asset collection"
          }), " — Collection guardrails, which apply to every brand that uses the collection."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The whole workspace"
          }), " — Ad sizes and channels, and everything on the ", _jsx(_components.strong, {
            children: "Emails"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-ad-guardrails",
        children: "Set ad guardrails"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Guardrails > Ads"
        }), " to decide what Ad Studio agents can do with images, how AI-generated ads are labeled, and which sizes ads are produced in."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-ads-collections.png",
          alt: "Ads tab with the brand guardrail toggle and collection guardrails for each asset collection"
        })
      }), "\n", _jsx(_components.h3, {
        id: "control-image-modifications",
        children: "Control image modifications"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Allow AI image generation or modification in your ads"
        }), " controls whether agents can generate or edit images in the brand's ads. It's on by default. Turn it off for a brand whose imagery can't be altered, such as licensed photos or images with strict usage rights."]
      }), "\n", _jsx(_components.p, {
        children: "When it's off, agents build ads only from your approved assets, layout, and copy. They don't generate new images or edit existing ones, and you can't edit the brand's collection guardrails until you turn the setting back on."
      }), "\n", _jsx(_components.h3, {
        id: "limit-changes-for-specific-asset-collections",
        children: "Limit changes for specific asset collections"
      }), "\n", _jsx(_components.p, {
        children: "Use collection guardrails when some images can be edited and others can't. For example, you might allow background changes on product shots but forbid any edits to photos of real customers. Collection guardrails apply to every brand that uses the collection, so a change for one brand changes it for all of them."
      }), "\n", _jsxs(_components.p, {
        children: ["Each asset collection assigned to the brand appears under ", _jsx(_components.strong, {
          children: "Collection guardrails"
        }), ". If no collections appear, assign a collection to the brand in ", _jsx(_components.a, {
          href: "/context-hub/assets",
          children: "Assets"
        }), " first. Select ", _jsx(_components.strong, {
          children: "Edit"
        }), " on a collection, then choose what agents can do with its images:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Turn off ", _jsx(_components.strong, {
            children: "Allow agents to generate or modify images"
          }), " when the collection's assets must appear in the final ad exactly as they are."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "People"
          }), ", decide whether agents can modify faces, bodies, or skin, or generate new people."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Scene"
          }), ", decide whether agents can modify backgrounds or swap products."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Touch-ups"
          }), ", decide whether agents can remove burned-in text, extend images beyond their original framing, or upscale images."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Custom guardrails"
          }), ", select ", _jsx(_components.strong, {
            children: "Add guardrail"
          }), " to write a rule in your own words, such as \"Never place text over the product.\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-collection-edit.png",
          alt: "Collection guardrails dialog with the image toggle, People, Scene, and Touch-ups actions, and a custom guardrail"
        })
      }), "\n", _jsx(_components.p, {
        children: "Write each custom guardrail as a single rule in plain language, and add one guardrail per rule so each is easy to change or remove later."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-custom-guardrail.png",
          alt: "Custom guardrails card with the rule Never place text over the product and an Add guardrail button"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-ai-disclosure-defaults",
        children: "Set AI disclosure defaults"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "AI disclosure defaults"
        }), " set how ads for this brand disclose AI-generated content when someone downloads or launches them. They're defaults, not locks: anyone can change them for a specific ad at download or launch time."]
      }), "\n", _jsx(_components.p, {
        children: "Choose defaults that match your legal and platform requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "On-image label"
          }), " — Adds a visible \"Contains AI-generated content\" label to the ad."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Embedded metadata"
          }), " — Adds IPTC/XMP metadata that platforms such as Meta can read."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For each, choose ", _jsx(_components.strong, {
          children: "Always"
        }), ", ", _jsx(_components.strong, {
          children: "Only when AI is used"
        }), ", or ", _jsx(_components.strong, {
          children: "Never"
        }), ". If you show an on-image label, set its ", _jsx(_components.strong, {
          children: "Label position"
        }), ", ", _jsx(_components.strong, {
          children: "Label font"
        }), ", and ", _jsx(_components.strong, {
          children: "Label font size"
        }), " so it fits your layouts. The font list includes your brand's typefaces."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-ai-disclosure.png",
          alt: "AI disclosure defaults with On-image label, Embedded metadata, and label position, font, and size settings"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-ad-sizes-and-channels",
        children: "Set ad sizes and channels"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Sizes and channels"
        }), " to set the sizes agents produce for each ad channel, such as Meta or Google Ads, and to tell agents how to resize creative to fit. These settings apply to all brands in your workspace."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Edit"
        }), " to open the editor. From there you can:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add a size to a channel, or edit an existing one. Give it an optional label and ", _jsx(_components.strong, {
            children: "Size guidance"
          }), ", and turn on ", _jsx(_components.strong, {
            children: "Set as default"
          }), " to make it the channel's default size."]
        }), "\n", _jsxs(_components.li, {
          children: ["Write ", _jsx(_components.strong, {
            children: "Resizing instructions"
          }), " for a channel, such as \"Keep the product in the center of the image.\" Agents follow them when they resize ads for that channel."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create custom channel"
          }), " for a placement that isn't listed."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-ad-sizes-channels.png",
          alt: "Sizes and channels with ad aspect ratios for each configured channel"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-email-guardrails",
        children: "Set email guardrails"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Context Hub > Guardrails > Emails"
        }), " to control how Lifecycle Studio lays out email, what it can change in images, and which HTML rules it applies. These settings apply to every email in your workspace."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-emails.png",
          alt: "Emails tab with Email layout, Email image guardrails, and Email best practices"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-the-email-layout",
        children: "Set the email layout"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.strong, {
          children: "Email layout"
        }), " to control how emails render on different screen sizes. For each setting, choose a preset or ", _jsx(_components.strong, {
          children: "Custom"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Set ", _jsx(_components.strong, {
            children: "Content width"
          }), " to the maximum width of the centered email content. On narrower screens, content shrinks to fit."]
        }), "\n", _jsxs(_components.li, {
          children: ["Set ", _jsx(_components.strong, {
            children: "Mobile breakpoint"
          }), " to the screen width at or below which emails switch to a stacked mobile layout."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Content width is often set to the same value as the mobile breakpoint. Email layout is saved on your default brand profile and applies to every brand."
      }), "\n", _jsx(_components.h3, {
        id: "control-email-image-modifications",
        children: "Control email image modifications"
      }), "\n", _jsxs(_components.p, {
        children: ["Turn off ", _jsx(_components.strong, {
          children: "Allow AI image generation or modification in your campaigns"
        }), " when agents shouldn't change images at all, such as when your emails use licensed photos. When it's off, campaigns use only your existing images."]
      }), "\n", _jsxs(_components.p, {
        children: ["When it's on, select ", _jsx(_components.strong, {
          children: "Edit"
        }), " to choose which changes agents can make:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Edit text"
          }), " — Change text burned into images, such as headlines, prices, or calls to action."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Resize images"
          }), " — Fit images to different formats or aspect ratios."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Blend elements from your content library"
          }), " — Replace or combine elements such as products, logos, or backgrounds, using only images from your approved content library."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Adjust colors and styling"
          }), " — Change colors, backgrounds, and the styling of existing elements."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Generate new visual elements"
          }), " — Create new elements, change scenes, or build compositions beyond your existing assets."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Allow only the changes your brand and legal teams are comfortable with. For example, allow text edits and resizing for seasonal banners, but leave new visual elements off if every image must come from approved photography."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-email-image-edit.png",
          alt: "Email image guardrails dialog with the image toggle and the five agent actions, each with an example"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-email-html-rules",
        children: "Set email HTML rules"
      }), "\n", _jsxs(_components.p, {
        children: ["Agents apply the ", _jsx(_components.strong, {
          children: "Email best practices"
        }), " rules when they check and fix email HTML. The rules cover areas such as compatibility, mobile responsiveness, dark mode, and accessibility."]
      }), "\n", _jsxs(_components.p, {
        children: ["All rules start enabled. Select ", _jsx(_components.strong, {
          children: "Edit"
        }), " to review them:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Turn off any rule that doesn't apply to your templates, such as a dark-mode rule your templates already handle another way."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add rule"
          }), " to write your own, with a ", _jsx(_components.strong, {
            children: "Rule name"
          }), " and ", _jsx(_components.strong, {
            children: "Description"
          }), " such as \"Keep product names unchanged.\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-email-best-practices.png",
          alt: "Email best practice rules drawer with each rule category and its enabled rules"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-agent-reviewers",
        children: "Create agent reviewers"
      }), "\n", _jsxs(_components.p, {
        children: ["Create an agent reviewer when your team wants an on-demand check of ad creative in Ad Studio, such as brand compliance or claims review. A reviewer responds only when a teammate @mentions it in a ", _jsx(_components.a, {
          href: "/ad-studio/refine#comment-on-an-ad",
          children: "comment on an ad"
        }), ". It reviews the ad using the skill you assign to it and replies in the comment thread."]
      }), "\n", _jsx(_components.p, {
        children: "Create one reviewer for each kind of check. Each reviewer belongs to one brand profile."
      }), "\n", _jsxs(_components.p, {
        children: ["Before you start, write the review process as a ", _jsx(_components.a, {
          href: "/context-hub/business-context#create-a-skill",
          children: "skill"
        }), " in ", _jsx(_components.strong, {
          children: "Context Hub > Knowledge > Skills"
        }), ". Only skills you create that apply to this brand, or to all brands, can guide a reviewer."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Context Hub > Guardrails > Agent reviewers"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create reviewer"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Agent name"
          }), ", enter the name your team will @mention, such as \"Brand reviewer.\""]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "Review skill"
          }), ", select the skill that guides the review."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create reviewer"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To change the reviewer's name or skill later, edit it from the same tab. If the reviewer's skill is deleted, edit the reviewer and select another skill. If you delete a reviewer, it stops reviewing creative, and its existing comments stay visible."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "context-hub/ch-guardrails-agent-reviewers.png",
          alt: "Agent reviewers tab with a Headline reviewer that uses the Writing Acme ad headlines skill, and a Create reviewer button"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/assets",
            children: "Manage assets"
          }), " to organize files into collections before you set collection guardrails for them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/context-hub/creative",
            children: "Set creative direction"
          }), " to add visual styles and copy references that guide generated images and copy."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
